import { AxiosInstance } from 'axios';

/**
 * Bridges auth token acquisition (which lives in React context) to the plain
 * Axios instances used by the API clients. `AuthTokenBridge` registers a getter
 * once the user is authenticated; each API client attaches an interceptor that
 * awaits it and sets the `Authorization: Bearer <token>` header on every request.
 */
type AccessTokenProvider = () => Promise<string | null>;

let accessTokenProvider: AccessTokenProvider | null = null;

/** Called by the auth bridge to supply the current access-token getter. */
export function registerAccessTokenProvider(provider: AccessTokenProvider | null): void {
  accessTokenProvider = provider;
}

/** Adds a request interceptor that attaches the active API bearer token when available. */
export function attachAuthInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use(async (config) => {
    if (config.headers.Authorization) return config;
    if (!accessTokenProvider) return config;
    try {
      const token = await accessTokenProvider();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // No valid token (e.g. session expired) — let the request proceed and the
      // API respond with 401, which the UI handles by prompting a re-login.
    }
    return config;
  });
}
